
import { HttpClient, HttpParams } from '@angular/common/http';
import { ElementRef, Inject, Injectable, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DeviceDetectorService } from 'ngx-device-detector';
import { fromEvent, Observable, Subject } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { ConfigService } from 'src/app/services/domain/config.service';
import { environment } from 'src/environments/environment';
import { AccessControl } from '../models/acess-control';
import { ClicksControl } from '../models/clicks-control';
import { LandingAccessControl } from './../models/landing-access-control';
import { LandingButtonClicks } from './../models/landing-button-clicks';
import { LandingSignUpTries } from './../models/landing-signUp-tries';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsEbarnService {
  private url: string;
  private unsub$: Subject<void>;
  private readonly appName = `cxm-frontend-${environment.nome.toLowerCase()}`;

  constructor(
    @Inject(DOCUMENT) public document: Document,
    private http: HttpClient,
    private router: Router,
    private deviceService: DeviceDetectorService,
    configService: ConfigService
  ) {
    this.url = configService.getUrlServiceNode() + '/api/analytics-ebarn';
  }

  public startListenEvents(elementListenClicks: ElementRef<HTMLElement>): void {
    this.unsub$ = new Subject<never>();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map<NavigationEnd, AccessControl>(event => ({
          page: event.url,
          ...this.getDataClient()
        })),
        switchMap(body => this.saveAccessControl(body)),
        catchError((err, caught) => caught),
        takeUntil(this.unsub$)
      )
      .subscribe();

    // fromEvent(elementListenClicks.nativeElement, 'click')
    //   .pipe(
    //     map<PointerEvent, ClicksControl>(this.getDataEvent.bind(this)),
    //     switchMap(body => this.saveClicksControl(body)),
    //     catchError((err, caught) => caught),
    //     takeUntil(this.unsub$)
    //   )
    //   .subscribe();
  }

  public stopListenEvents(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private getDataEvent(event: PointerEvent): ClicksControl {
    return {
      page: this.router.url,
      tagName: (event.target as HTMLElement).tagName,
      className:
        typeof (event.target as HTMLElement).className === 'object'
          ? ''
          : (event.target as HTMLElement).className,
      clientX: event.clientX,
      clientY: event.clientY,
      scrollX: event.view.scrollX,
      scrollY: event.view.scrollY,
      innerWidth: event.view.innerWidth,
      innerHeight: event.view.innerHeight,
      outerWidth: event.view.outerWidth,
      outerHeight: event.view.outerHeight,
      timestamp: event.timeStamp,
      ...this.getDataClient()
    };
  }

  private getDataClient(): Omit<AccessControl, 'page'> {
    const deviceInfo = this.deviceService.getDeviceInfo();
    return {
      device: {
        ...deviceInfo,
        osVersion: deviceInfo.os_version,
        browserVersion: deviceInfo.browser_version
      },
      userAgent: this.deviceService.userAgent,
      cookie: this.document.cookie,
      app: this.appName,
      url: this.window.location.href
    };
  }

  private saveAccessControl(body: AccessControl): Observable<void> {
    return this.http.post<void>(`${this.url}/access-control`, body).pipe(take(1), shareReplay());
  }

  private saveClicksControl(body: ClicksControl): Observable<void> {
    return this.http.post<void>(`${this.url}/clicks-control`, body).pipe(take(1), shareReplay());
  }

  public landingAccessControlCreate(body: LandingAccessControl): Observable<LandingAccessControl> {
    return this.http
      .post<LandingAccessControl>(`${this.url}/landing-access-control`, body)
      .pipe(take(1), shareReplay());
  }

  public landingAccessControlFindByRefUrl(refUrl: string): Observable<LandingAccessControl[]> {
    const params = new HttpParams().append('refUrl', refUrl);
    return this.http
      .get<LandingAccessControl[]>(`${this.url}/landing-access-control/findByRefUrl`, {
        params
      })
      .pipe(take(1), shareReplay());
  }

  public landingButtonClicksCreate(body: LandingButtonClicks): Observable<LandingButtonClicks> {
    return this.http
      .post<LandingButtonClicks>(`${this.url}/landing-button-clicks`, body)
      .pipe(take(1), shareReplay());
  }

  public landingButtonClicksFindByRefUrl(refUrl: string): Observable<LandingButtonClicks[]> {
    const params = new HttpParams().append('refUrl', refUrl);
    return this.http
      .get<LandingButtonClicks[]>(`${this.url}/landing-button-clicks/findByRefUrl`, {
        params
      })
      .pipe(take(1), shareReplay());
  }

  public landingSignUpTriesCreate(body: LandingSignUpTries): Observable<LandingSignUpTries> {
    return this.http
      .post<LandingSignUpTries>(`${this.url}/landing-signUp-tries`, body)
      .pipe(take(1), shareReplay());
  }

  public landingSignUpTriesFindByRefUrl(refUrl: string): Observable<LandingSignUpTries[]> {
    const params = new HttpParams().append('refUrl', refUrl);
    return this.http
      .get<LandingSignUpTries[]>(`${this.url}/landing-signUp-tries/findByRefUrl`, {
        params
      })
      .pipe(take(1), shareReplay());
  }

  get window(): Window | null {
    return this.document.defaultView;
  }
}
